import AppHeader from "@/components/AppHeader";
import InformationDisplay, { formatInformationTime } from "@/components/InformationDisplay";
import EditorialDiagnosticsDisplay from "@/components/EditorialDiagnosticsDisplay";
import NewsRelevanceDisplay from "@/components/NewsRelevanceDisplay";
import { useIdeas } from "@/hooks/useNews";
import { type Idea, type IdeasEvidenceSource } from "@/lib/types";
import { Helmet } from "react-helmet-async";
import { ExternalLink, Lightbulb, Loader2, RefreshCw, XCircle } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { projectIdeasRetention } from "@shared/ideasRetention";
import type { IdeasHttpResponse, IdeasSaveProofV1, SaveIdeasRequestV1, SaveIdeasResultV1 } from "@shared/ideasRetention";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";

// ---------------------------------------------------------------------------
// Conviction badge
// ---------------------------------------------------------------------------

const CONVICTION_COLOR: Record<Idea["conviction"], { bg: string; text: string }> = {
  high: { bg: "#ff9100", text: "#000" },
  medium: { bg: "#2a2a2a", text: "#aaa" },
  low: { bg: "#111", text: "#555" },
};

function ConvictionBadge({ conviction }: { conviction: Idea["conviction"] }) {
  const c = CONVICTION_COLOR[conviction];
  return (
    <span
      style={{
        background: c.bg,
        color: c.text,
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: "0.1em",
        padding: "2px 7px",
        textTransform: "uppercase",
        display: "inline-block",
      }}
    >
      {conviction}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Idea card
// ---------------------------------------------------------------------------

function IdeaCard({ idea }: { idea: Idea }) {
  return (
    <div
      style={{
        border: "1px solid #1a1a1a",
        background: "#070707",
        padding: "16px 18px",
        marginBottom: 12,
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
        <Lightbulb size={14} color="#ff9100" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
            <ConvictionBadge conviction={idea.conviction} />
            <span style={{ color: "#666", fontSize: 9 }}>editorial conviction · unvalidated, not probability</span>
          </div>
          <h2 style={{ color: "#fff", fontSize: 14, fontWeight: 700, margin: "0 0 8px", lineHeight: 1.3 }}>
            {idea.title}
          </h2>
          <p style={{ color: "#aaa", fontSize: 12, lineHeight: 1.6, margin: "0 0 6px" }}>
            {idea.rationale}
          </p>
          {idea.marketAngle && (
            <p style={{ color: "#888", fontSize: 12, margin: "0 0 10px" }}>
              <span style={{ color: "#ff9100", fontWeight: 700 }}>Watch: </span>
              {idea.marketAngle}
            </p>
          )}
        </div>
      </div>

      {/* Citations */}
      {idea.citations.length > 0 && (
        <div style={{ borderTop: "1px solid #111", paddingTop: 10, marginLeft: 24 }}>
          <p style={{ color: "#444", fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", margin: "0 0 6px" }}>
            Source Citations
          </p>
          {idea.citations.map((c) => (
            <div key={c.signalId} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 5 }}>
              <span
                style={{
                  color: "#555",
                  fontSize: 9,
                  border: "1px solid #222",
                  padding: "1px 4px",
                  flexShrink: 0,
                  fontWeight: 800,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  marginTop: 1,
                }}
              >
                source grade: {c.confidence}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ color: "#ff9100", fontSize: 10, fontWeight: 700 }}>{c.source}</span>
                <span style={{ color: "#444", fontSize: 10 }}> · {c.category}</span>
                <div style={{ color: "#666", fontSize: 11, lineHeight: 1.3, marginTop: 2 }}>
                  {c.title.slice(0, 100)}{c.title.length > 100 ? "…" : ""}
                  {c.url && (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#444", marginLeft: 6, display: "inline-flex", alignItems: "center", gap: 2 }}
                    >
                      <ExternalLink size={9} />
                    </a>
                  )}
                </div>
                <div style={{ color: "#888", fontSize: 10, marginTop: 4 }}>Evidence at idea generation:</div>
                <NewsRelevanceDisplay newsEvidence={c.newsEvidence} />
                <InformationDisplay information={c.information} captured />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FrozenIdeasEvidence({ signals }: { signals: IdeasEvidenceSource[] }) {
  const boundedSignals = signals.slice(0, 15);
  return (
    <section aria-labelledby="ideas-evidence-heading" style={{ border: "1px solid #222", padding: "14px 16px", marginBottom: 18 }}>
      <h2 id="ideas-evidence-heading" style={{ color: "#ddd", fontSize: 13, margin: "0 0 6px" }}>
        Frozen source statements ({boundedSignals.length})
      </h2>
      <p style={{ color: "#888", fontSize: 11, lineHeight: 1.5, margin: "0 0 12px" }}>
        These are the source records used for selection, up to 15 records.
      </p>
      <ol style={{ margin: 0, paddingLeft: 22 }}>
        {boundedSignals.map((source) => (
          <li key={source.id} style={{ color: "#aaa", fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
            <div style={{ color: "#ff9100", fontWeight: 700 }}>{source.source}</div>
            <div style={{ color: "#ddd", fontWeight: 700 }}>{source.title}</div>
            {source.observedAt && <div>Observed: {formatInformationTime(source.observedAt)}</div>}
            {source.summary && <p style={{ margin: "4px 0" }}>{source.summary}</p>}
            <InformationDisplay information={source.information} captured />
            {source.newsEvidence?.map((article, index) => (
              <div key={`${article.id ?? source.id}:${index}`} style={{ borderLeft: "2px solid #333", margin: "8px 0 0 8px", paddingLeft: 8 }}>
                <div style={{ color: "#bbb" }}>{article.publisher}: {article.title}</div>
                {article.description && <div>{article.description}</div>}
                {article.information && <InformationDisplay information={article.information} captured />}
              </div>
            ))}
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ideasResponseWithoutSaveFields(data: IdeasHttpResponse) {
  const response = { ...data } as IdeasHttpResponse & Record<string, unknown>;
  delete response.saveProof;
  delete response.saveAvailability;
  return response;
}

function exclusionDisplay(item: { path: string; reason: string }, response: IdeasHttpResponse, preview: ReturnType<typeof projectIdeasRetention>): string {
  const signalIds = new Set<string>();
  const signalMatch = item.path.match(/^evidence\.signals\[(\d+)\]/);
  const citationMatch = item.path.match(/^ideas\[(\d+)\]\.citations\[(\d+)\]/);
  const ideaMatch = item.path.match(/^ideas\[(\d+)\]/);
  if (signalMatch) {
    const source = response.evidence?.signals[Number(signalMatch[1])];
    if (source) signalIds.add(source.id);
  } else if (citationMatch) {
    const citation = response.ideas[Number(citationMatch[1])]?.citations[Number(citationMatch[2])];
    if (citation) signalIds.add(citation.signalId);
  } else if (ideaMatch) {
    const idea = response.ideas[Number(ideaMatch[1])];
    const dependency = idea && preview.dependencies.find((entry) => entry.ideaId === idea.id);
    dependency?.signalIds.forEach((id) => signalIds.add(id));
  }
  const sources = [...signalIds].map((id) => {
    const source = response.evidence?.signals.find((entry) => entry.id === id);
    const citation = response.ideas.flatMap((idea) => idea.citations).find((entry) => entry.signalId === id);
    return source ? `${source.source}: ${source.title}` : citation ? `${citation.source}: ${citation.title}` : id;
  });
  const sourceLabel = sources.length ? `Source(s): ${sources.join("; ")}. ` : "Source attribution is unavailable for this field. ";
  return `${sourceLabel}Field: ${item.path}. Limit: ${item.reason.replaceAll("-", " ")}.`;
}

export function saveErrorMessage(code: string | undefined): string {
  switch (code) {
    case "proof_expired": return "This save ticket expired. The Ideas shown here are unchanged. Use Refresh only if you want a new Ideas result.";
    case "proof_not_yet_valid": return "This save ticket is not valid yet. The displayed Ideas remain unchanged.";
    case "metadata_ack_required": return "Confirm the listed limits before saving this metadata-only copy.";
    case "retention_not_allowed": return "This result has no meaningful content that can be retained.";
    case "owner_required": return "Sign in or enable this browser's private client identity before saving.";
    case "signing_unavailable": return "Saving is unavailable because the server cannot issue a save ticket.";
    case "request_too_large":
    case "capture_too_large": return "This result is too large to save.";
    case "archive_quota_exhausted": return "The private report archive has no available space for this result.";
    case "archive_busy": return "Save completion could not be confirmed because the private archive is busy. Check Reports before retrying.";
    case "archive_unavailable":
    case "archive_failed": return "Save completion could not be confirmed for the private archive. Check Reports before retrying.";
    case "origin_rejected": return "The save request was rejected by the site security check.";
    case "idempotency_conflict": return "This save ticket is already bound to a different saved result.";
    case "invalid_save_request": return "The save ticket no longer matches the displayed result. Refresh only if you want a new Ideas result.";
    case "not_found": return "The saved result is not available to this owner.";
    default: return "Save completion could not be confirmed. Check Reports before retrying.";
  }
}

function availabilityMessage(data: IdeasHttpResponse): string | null {
  if (data.saveProof) return null;
  switch (data.saveAvailability) {
    case "owner-required": return "Saving needs the current owner identity. The Ideas result is still available to read.";
    case "signing-unavailable": return "Saving is unavailable because the server cannot issue a save ticket.";
    case "payload-too-large": return "This Ideas response is too large to save. No content was shortened.";
    case "available": return "The save ticket is missing. Refresh only if you want a new Ideas result.";
    default: return "A save ticket is unavailable for this Ideas response.";
  }
}

export function createIdeasSaveRequest(
  data: IdeasHttpResponse,
  acknowledgeMetadataOnly: boolean,
): SaveIdeasRequestV1 {
  const proof = data.saveProof as IdeasSaveProofV1 | undefined;
  if (!proof) throw new Error("missing_save_ticket");
  const response = ideasResponseWithoutSaveFields(data);
  return {
    proof,
    response,
    ...(acknowledgeMetadataOnly ? { acknowledgeMetadataOnly: true as const } : {}),
  };
}

async function saveIdeasSnapshot(
  data: IdeasHttpResponse,
  acknowledgeMetadataOnly: boolean,
): Promise<SaveIdeasResultV1> {
  const body = createIdeasSaveRequest(data, acknowledgeMetadataOnly);
  const res = await fetch("/api/ideas/save", {
    method: "POST",
    credentials: "include",
    headers: { ...getClientIdentityHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = await res.json().catch(() => ({})) as { code?: string; error?: string } & Partial<SaveIdeasResultV1>;
  if (!res.ok) throw new Error(payload.code ?? payload.error ?? "save_failed");
  return payload as SaveIdeasResultV1;
}

const Ideas: React.FC = () => {
  const { data, isLoading, isError, refetch, isFetching } = useIdeas();
  const [metadataAcknowledgement, setMetadataAcknowledgement] = useState<{ key: string; checked: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState<{ key: string; message: string; reportId: string | null } | null>(null);
  const response = useMemo(() => data ? ideasResponseWithoutSaveFields(data) : null, [data]);
  const preview = useMemo(() => {
    if (!response || !data?.saveProof) return null;
    try {
      return projectIdeasRetention(response, { nwsLineage: "trusted-fixed-acquisition-v1" });
    } catch {
      return null;
    }
  }, [response, data?.saveProof]);
  const acknowledgementKey = useMemo(() => data && response && preview
    ? JSON.stringify({ response, proof: data.saveProof ?? null, preview })
    : "", [data, response, preview]);
  const acknowledgeMetadataOnly = metadataAcknowledgement?.key === acknowledgementKey && metadataAcknowledgement.checked;

  useEffect(() => {
    setSaveNotice(null);
  }, [acknowledgementKey]);

  const onSave = async () => {
    if (!data?.saveProof || !preview || (preview.contentCoverage === "metadata-only" && !acknowledgeMetadataOnly) || !preview.meaningful) return;
    const requestKey = acknowledgementKey;
    setSaving(true);
    setSaveNotice(null);
    try {
      const result = await saveIdeasSnapshot(data, preview.contentCoverage === "metadata-only");
      setSaveNotice({ key: requestKey, reportId: result.reportId, message: result.contentCoverage === "complete"
        ? "Complete Ideas result saved privately as a historical report."
        : "Metadata-only Ideas copy saved privately. Excluded text cannot be replayed from the archive." });
    } catch (error) {
      setSaveNotice({ key: requestKey, reportId: null, message: saveErrorMessage(error instanceof Error ? error.message : undefined) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Ideas — Source-Backed Market Ideas | Tylee Smart Markets</title>
        <meta name="description" content="Market ideas with cited source records. Each idea cites dated source records from NOAA, USGS, GDELT, WHO, and finance data providers." />
        <link rel="canonical" href="https://tyl.ee/ideas" />
      </Helmet>

      <div className="tsm-home-shell theme-amber" style={{ minHeight: "100vh" }}>
        <a href="#ideas-main" className="skip-link">Skip to main content</a>
        <AppHeader brandText="Ideas" />

        <main id="ideas-main" style={{ maxWidth: 820, margin: "0 auto", padding: "24px 18px" }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
            <div>
              <p style={{ color: "#ff9100", fontWeight: 800, letterSpacing: "0.08em", margin: 0, fontSize: 11, textTransform: "uppercase" }}>
                TSM IDEAS
              </p>
              <h1 style={{ color: "#fff", fontSize: 22, fontWeight: 800, margin: "4px 0 0", lineHeight: 1.1 }}>
                Source-Cited Market Ideas
              </h1>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
              {data && <span style={{ color: "#777", fontSize: 11 }}>
                {data.diagnostics?.resolvedProvider
                  ? `${data.diagnostics.resolvedProvider} · ${data.diagnostics.resolvedModel ?? "model ID not supplied"}`
                  : data.diagnostics?.outcome === "heuristic"
                    ? "Heuristic selection"
                    : data.diagnostics ? "No model result" : "Processing details unavailable"}
              </span>}
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                style={{
                  background: "none",
                  border: "1px solid #333",
                  color: "#888",
                  padding: "5px 10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 11,
                }}
              >
                <RefreshCw size={11} className={isFetching ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>

          {/* Mode note */}
          <EditorialDiagnosticsDisplay diagnostics={data?.diagnostics} />
          {data && <p style={{ color: "#777", fontSize: 11, margin: "0 0 14px" }}>
            Selection does not establish source agreement or complete coverage.
          </p>}

          {data?.evidence?.signals && <FrozenIdeasEvidence signals={data.evidence.signals} />}

          {data && (data.saveProof || data.saveAvailability) && (
            <section aria-labelledby="ideas-save-heading" style={{ border: "1px solid #282828", padding: "14px 16px", margin: "0 0 18px", background: "#080808" }}>
              <h2 id="ideas-save-heading" style={{ color: "#ddd", fontSize: 13, margin: "0 0 6px" }}>Save this Ideas result privately</h2>
              <p style={{ color: "#999", fontSize: 11, lineHeight: 1.5, margin: "0 0 8px" }}>
                This creates a private historical record of the current result. Excluded fields are listed below. Saving does not refresh or regenerate Ideas.
              </p>
              {preview && <p style={{ color: "#bbb", fontSize: 11, margin: "0 0 8px" }}>
                Preview: {preview.contentCoverage === "complete" ? "complete response" : "metadata-only copy"}.
                {preview.contentCoverage === "metadata-only" ? " Excluded text will not be stored or replayable." : " All response content is retained under the current policy."}
              </p>}
              {preview && preview.notRetained.length > 0 && (
                <div aria-label="Fields excluded from saved report" style={{ color: "#aaa", fontSize: 11, marginBottom: 10 }}>
                  <strong>Excluded fields and limits</strong>
                  <ul style={{ margin: "4px 0 8px", paddingLeft: 20 }}>
                    {preview.notRetained.map((item, index) => (
                      <li key={`${item.path}:${item.reason}:${index}`}>{exclusionDisplay(item, response!, preview)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {preview?.contentCoverage === "metadata-only" && (
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, color: "#ccc", fontSize: 11, lineHeight: 1.5, marginBottom: 10 }}>
                  <input
                    type="checkbox"
                    checked={acknowledgeMetadataOnly}
                    onChange={(event) => setMetadataAcknowledgement({ key: acknowledgementKey, checked: event.target.checked })}
                    disabled={saving}
                    aria-describedby="ideas-save-limits"
                  />
                  <span id="ideas-save-limits">I understand that this save keeps only the permitted metadata and that excluded text cannot be recovered from the saved report.</span>
                </label>
              )}
              {data.saveProof && preview ? (
                <button type="button" onClick={onSave} disabled={saving || !preview.meaningful || (preview.contentCoverage === "metadata-only" && !acknowledgeMetadataOnly)} style={{ background: "#ff9100", border: "1px solid #ff9100", color: "#111", fontWeight: 700, padding: "7px 12px", cursor: "pointer" }}>
                  {saving ? "Saving…" : "Save private report"}
                </button>
              ) : <p role="status" style={{ color: "#d5a45d", fontSize: 11 }}>{data.saveProof ? "This response could not be checked safely for retention. It cannot be saved." : availabilityMessage(data)}</p>}
              {preview && !preview.meaningful && <p role="status" style={{ color: "#d5a45d", fontSize: 11 }}>This result has no meaningful retained Idea citation to save.</p>}
              {saveNotice?.key === acknowledgementKey && <p role="status" aria-live="polite" style={{ color: "#bbb", fontSize: 11 }}>{saveNotice.message}{saveNotice.reportId && <> <a href={`/reports?id=${encodeURIComponent(saveNotice.reportId)}`}>Open historical report</a></>}</p>}
            </section>
          )}

          {/* Body */}
          {isLoading ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", color: "#555", padding: "40px 0" }}>
              <Loader2 size={16} className="animate-spin" />
              <span style={{ fontSize: 13 }}>Generating ideas from source records…</span>
            </div>
          ) : isError ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", color: "#ff4444", padding: "40px 0" }}>
              <XCircle size={16} />
              <span style={{ fontSize: 13 }}>Failed to load ideas. Check server logs.</span>
            </div>
          ) : !data?.ideas.length ? (
            <div style={{ color: "#555", padding: "40px 0", fontSize: 13 }}>
              No ideas available. Signals may still be loading.
            </div>
          ) : (
            <div>
              {data.ideas.map((idea) => (
                <IdeaCard key={idea.id} idea={idea} />
              ))}
            </div>
          )}

          {/* Footer */}
          {data && (
            <div style={{ marginTop: 24, borderTop: "1px solid #1a1a1a", paddingTop: 12 }}>
              <p style={{ color: "#444", fontSize: 10, margin: 0 }}>
                Ideas assembled {formatInformationTime(data.generatedAt)} · {data.ideas.length} ideas · {data.diagnostics ? `outcome: ${data.diagnostics.outcome}` : "processing details unavailable"}
              </p>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default Ideas;
